import { Command, Option } from "clipanion";
import { DISCLOSURE_TIER1 } from "@m8t-stack/telemetry-contract";
import { M8tCommand } from "../../lib/m8t-command.js";
import { getAzAccount, getCallerObjectId } from "../../lib/auth.js";
import { LocalCliError } from "../../lib/errors.js";
import { SPEND_DISCLOSURE } from "../../lib/spend-disclosure.js";
import { colors } from "../../lib/output.js";
import { buildPreflightBanner, checkAppRegCapability, checkSubScopeAdmin } from "../../lib/bootstrap-preflight.js";
import { buildPrereqDeps, INSTALL_REASONING_MODEL } from "../../lib/prereq-deps.js";
import { runInstallPhase } from "../../lib/prereq-run.js";
import type { PrereqResult } from "../../lib/prereqs.js";

/**
 * Rows this command has ALREADY reported itself, above, with its own loud copy —
 * the caller checks it runs and hard-stops on before touching the subscription.
 * Rendering them again from the verdict would report each twice.
 *
 * It is a blocklist on purpose. It used to be a whitelist of the two substrate
 * rows to show, which meant every prerequisite added afterwards was born
 * invisible on the paved path: `soft-deleted-account` could be wired into
 * `m8t prereqs`, refuse correctly there, and still let this command — the one a
 * founder actually runs — pass green. Excluding the known duplicates and showing
 * the rest fails safe in the other direction.
 *
 * Each exclusion is justified by a sibling's behaviour, so preflight.test.ts pins
 * that behaviour: if a caller check stops printing, the exclusion breaks loudly.
 */
export const PREFLIGHT_SELF_REPORTED: readonly string[] = ["az-signed-in", "subscription-admin", "app-registration"];

export function preflightRenderable(results: readonly PrereqResult[]): PrereqResult[] {
  return results.filter((r) => !PREFLIGHT_SELF_REPORTED.includes(r.slug));
}

export class BootstrapPreflightCommand extends M8tCommand {
  static paths = [["bootstrap", "preflight"]];
  static usage = Command.Usage({
    description: "Loudly verify you can install m8t (Owner/UAA + directory admin) and hard-stop if not.",
    details:
      "Step 1 of `m8t bootstrap`. Prints an unmissable admin-credentials notice, then checks: Owner or User Access Administrator at subscription scope (hard requirement), directory-admin capability to register the app (or pass --client-id), every Azure resource provider the install uses (registering any that are missing), soft-deleted Cognitive Services accounts (they keep their model quota until purged, so quota can read free and the model deployment still fail), and — with --location — model quota in the target region. Exits non-zero with the exact failing check + remedy. `m8t prereqs` runs the same substrate checks on their own.",
    examples: [
      ["Run the preflight", "$0 bootstrap preflight"],
      ["Check quota for the region you will install into", "$0 bootstrap preflight --location eastus2"],
      ["BYO app registration (directory guests)", "$0 bootstrap preflight --client-id <appId>"],
    ],
  });

  clientId = Option.String("--client-id");
  subscription = Option.String("--subscription");
  location = Option.String("--location", {
    description: "Target region. Enables the model-quota check — without it, quota is not verified.",
  });

  async executeCommand(): Promise<number> {
    const clientId = typeof this.clientId === "string" ? this.clientId : undefined;
    const account = await getAzAccount();
    const subscriptionId = (typeof this.subscription === "string" ? this.subscription : undefined) ?? account.subscriptionId;
    const oid = await getCallerObjectId();

    this.context.stdout.write(SPEND_DISCLOSURE + "\n\n");
    this.context.stdout.write(buildPreflightBanner({ upn: account.upn, tenantId: account.tenantId, subscriptionId }) + "\n");
    this.context.stdout.write(`\n${colors.dim(DISCLOSURE_TIER1)}\n`);
    this.context.stdout.write("Checking your access…\n");

    const sub = await checkSubScopeAdmin(oid, subscriptionId);
    this.context.stdout.write(line(sub.ok, `${sub.role ?? "Owner/UAA"} at subscription scope`, "resource + role-assignment rights"));
    if (!sub.ok) {
      this.context.stderr.write(failBlock("You are NOT an Owner or User Access Administrator at subscription scope.", "Ask a subscription Owner to run this install — the managed identity grant is unavoidable."));
      throw new LocalCliError({ code: "BOOTSTRAP_NOT_SUB_ADMIN", message: "Caller is not Owner/User Access Administrator at subscription scope." });
    }

    if (!clientId) {
      const appReg = await checkAppRegCapability(oid);
      this.context.stdout.write(line(appReg.ok, "Directory admin — can register the m8t app", "first-run app registration"));
      if (!appReg.ok) {
        this.context.stderr.write(failBlock(
          appReg.inconclusive
            ? "Couldn't verify your directory admin role (Microsoft Graph denied the read — common for guest accounts)."
            : "You don't hold a directory admin role (Application / Cloud Application / Global Administrator).",
          "Re-run with --client-id <appId> using a ready app registration from an admin.",
        ));
        throw new LocalCliError({ code: "BOOTSTRAP_NO_APP_REG_CAP", message: "Caller cannot register the app; supply --client-id." });
      }
    } else {
      this.context.stdout.write(line(true, `Using your app registration ${clientId}`, "--client-id supplied; skipping the directory-admin check"));
    }

    // Resource providers + model quota. These used to be discovered the hard way:
    // four namespaces were registered INSIDE the installer container behind a
    // `|| true`, five more were never registered anywhere, and quota was only
    // judged mid-install — after the spend had started. Both now run here, before
    // anything is created, and REFUSE only on a stable, deterministically-observed
    // blocker (see lib/prereqs.ts for the posture).
    const region = typeof this.location === "string" ? this.location : undefined;
    const verdict = await runInstallPhase(buildPrereqDeps({ subscription: subscriptionId }), {
      fix: true,
      subscription: subscriptionId,
      // BOTH region and model are required for the quota check to run at all.
      // Passing only the region silently skipped it while --location implied it
      // had happened.
      ...(region ? { region, model: INSTALL_REASONING_MODEL } : {}),
      ...(clientId ? { clientId } : {}),
      onProgress: (m) => { this.context.stderr.write(colors.dim(`  ${m}\n`)); },
    });
    // Everything the caller checks above did NOT already report, in its own loud
    // copy. See PREFLIGHT_SELF_REPORTED: this is a blocklist rather than a list
    // of rows to show, so a prerequisite added to PREREQS surfaces here without
    // anyone remembering to come back and add it.
    for (const r of preflightRenderable(verdict.results)) {
      this.context.stdout.write(statusLine(r.status, r.title, r.detail));
      // A warn or a skip is not a pass, and it carries a remedy worth acting on
      // (the region holding a soft-deleted account; a provider still
      // registering). Printing the tick and dropping the remedy was how a row
      // that had something to say became indistinguishable from one that did not.
      if ((r.status === "warn" || r.status === "skipped") && r.remedy) {
        this.context.stdout.write(`      ${colors.hint("if this is your region:")} ${r.remedy}\n`);
      }
      if (r.status === "fail") {
        this.context.stderr.write(failBlock(r.detail, r.remedy ?? "See guides/prerequisites.md."));
        throw new LocalCliError({ code: `BOOTSTRAP_PREREQ_${r.slug.toUpperCase().replace(/-/g, "_")}`, message: r.detail, hint: r.remedy });
      }
    }
    if (verdict.unchecked.length > 0) {
      this.context.stdout.write(
        colors.dim(`  Not checked (you may still hit them): ${verdict.unchecked.join(", ")} — see guides/prerequisites.md\n`),
      );
    }

    this.context.stdout.write(
      `\n${colors.success("✅ Preflight passed.")} Here is the ONE consent step you'll clear, then walk away:\n` +
      `     1. The az login you just did.            ${colors.dim("← that's it for now.")}\n` +
      colors.dim("   Everything else (app registration, the installer identity + its roles,\n   the cloud installer itself) runs automatically and cleans itself up.\n") +
      `\n${colors.hint("next:")} m8t bootstrap launch --location <region>${clientId ? ` --client-id ${clientId}` : ""}\n`,
    );
    return 0;
  }
}

function line(ok: boolean, label: string, note: string): string {
  const mark = ok ? colors.success("✓") : colors.error("✗");
  return `  ${mark} ${label.padEnd(50)} ${colors.dim(`(${note})`)}\n`;
}

/**
 * Three marks, not two. `line(status !== "fail", …)` gave a warn and a skip the
 * same green tick as a pass, under a title that affirmatively claims the thing
 * is fine — so "there is a soft-deleted account, I just cannot judge it against
 * a region you have not named" rendered identically to "there is none". A tick
 * over an unverified row is the false green this whole preflight exists to stop.
 */
function statusLine(status: PrereqResult["status"], label: string, note: string): string {
  if (status === "fail") return line(false, label, note);
  if (status === "pass" || status === "fixed") return line(true, label, note);
  return `  ${colors.hint("!")} ${label.padEnd(50)} ${colors.dim(`(${note})`)}\n`;
}
function failBlock(why: string, remedy: string): string {
  return (
    `\n${colors.error("⛔ CANNOT PROCEED")}\n` +
    `${colors.error("  " + why)}\n` +
    `  ${colors.hint("remedy:")} ${remedy}\n` +
    `  ${colors.hint("Nothing has been created. Re-run after fixing the above.")}\n\n`
  );
}
